import { clsx } from 'clsx';
import type { DocumentTraceabilityTreeProps, TraceabilityTreeNode } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';

interface DisplayNode {
  node: TraceabilityTreeNode;
  depth: number;
  isLastSibling: boolean;
  connectorPath: boolean[];
}

function buildDisplayList(root: TraceabilityTreeNode): DisplayNode[] {
  const result: DisplayNode[] = [];

  function walkAncestors(node: TraceabilityTreeNode, depth: number, connectorPath: boolean[]): void {
    for (const parent of node.parents) {
      walkAncestors(parent, depth, connectorPath);
    }
    result.push({ node, depth, isLastSibling: true, connectorPath });
  }

  function walkDescendants(
    nodes: TraceabilityTreeNode[],
    depth: number,
    connectorPath: boolean[],
  ): void {
    nodes.forEach((node, index) => {
      const isLast = index === nodes.length - 1;
      const path = [...connectorPath, !isLast];
      result.push({ node, depth, isLastSibling: isLast, connectorPath: path });
      if (node.children.length > 0) {
        walkDescendants(node.children, depth + 1, path);
      }
    });
  }

  for (const parent of root.parents) {
    walkAncestors(parent, 0, []);
  }

  result.push({ node: root, depth: root.parents.length > 0 ? 1 : 0, isLastSibling: true, connectorPath: [] });

  if (root.children.length > 0) {
    const rootDepth = root.parents.length > 0 ? 1 : 0;
    walkDescendants(root.children, rootDepth + 1, []);
  }

  return result;
}

function SmallDocIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M3 1.75C3 1.336 3.336 1 3.75 1H8.25L11 3.75V12.25C11 12.664 10.664 13 10.25 13H3.75C3.336 13 3 12.664 3 12.25V1.75Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d="M8 1V4H11"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M5 7H9M5 9.5H8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ExclamationIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="text-red-400"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 7v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="16" r="0.75" fill="currentColor" />
    </svg>
  );
}

function DocLinkIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="text-content-tertiary dark:text-dark-content-tertiary"
    >
      <path
        d="M9 12h6M9 8h4M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 17a2 2 0 104 0 2 2 0 00-4 0zM17.5 17h1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LoadingSkeleton() {
  const { t } = useMasterDocumentTranslation();
  return (
    <div className="space-y-2 p-4" aria-busy="true" aria-label={t('master_document.crossing.traceabilityLoading')}>
      <div className="flex items-center gap-2">
        <div className="animate-pulse w-full h-8 bg-background-secondary dark:bg-dark-bg-secondary rounded-lg" />
      </div>
      <div className="flex items-center gap-2 pl-5">
        <div className="animate-pulse flex-1 h-8 bg-background-secondary dark:bg-dark-bg-secondary rounded-lg" />
      </div>
      <div className="flex items-center gap-2 pl-10">
        <div className="animate-pulse flex-1 h-8 bg-background-secondary dark:bg-dark-bg-secondary rounded-lg" style={{ width: 'calc(100% - 2.5rem)' }} />
      </div>
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: string; onRetry?: () => void }) {
  const { t } = useMasterDocumentTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-8 px-4">
      <ExclamationIcon />
      <p className="text-sm text-content-secondary dark:text-dark-content-secondary text-center">
        {error}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={clsx(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md',
            'text-sm font-semibold',
            'bg-primary-custom-600 text-white',
            'hover:bg-primary-custom-500',
            'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
            'dark:bg-dark-bg-inverse dark:text-dark-content-inverse',
            'dark:hover:bg-dark-bg-inverse/90',
            'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            'transition-colors duration-150',
          )}
        >
          {t('master_document.chrome.retry')}
        </button>
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useMasterDocumentTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-8 px-4">
      <DocLinkIcon />
      <p className="text-sm text-content-secondary dark:text-dark-content-secondary text-center">
        {t('master_document.crossing.noRelations')}
      </p>
    </div>
  );
}

function ConnectorBlock({
  drawVertical,
  drawElbow,
}: {
  drawVertical: boolean;
  drawElbow: boolean;
}) {
  return (
    <div className="w-5 h-10 flex-shrink-0 relative">
      {drawVertical && !drawElbow && (
        <div className="absolute left-2 top-0 bottom-0 border-l-2 border-border-primary dark:border-dark-border-primary" />
      )}
      {drawElbow && (
        <>
          <div className="absolute left-2 top-0 h-5 border-l-2 border-border-primary dark:border-dark-border-primary" />
          <div className="absolute left-2 top-5 w-2.5 border-b-2 border-border-primary dark:border-dark-border-primary" />
        </>
      )}
    </div>
  );
}

function TreeNodeRow({
  displayNode,
  onNodeClick,
}: {
  displayNode: DisplayNode;
  onNodeClick?: (nodeId: string) => void;
}) {
  const { t } = useMasterDocumentTranslation();
  const { node, depth, connectorPath } = displayNode;

  const pct =
    node.quantityOrdered && node.quantityOrdered > 0
      ? Math.min(100, ((node.quantityFulfilled ?? 0) / node.quantityOrdered) * 100)
      : 0;

  const isClickable = !!onNodeClick && !node.isCurrent;

  return (
    <div className="flex items-start gap-0">
      {Array.from({ length: depth }).map((_, level) => {
        const isLastLevel = level === depth - 1;
        if (isLastLevel) {
          return (
            <ConnectorBlock key={level} drawVertical={false} drawElbow={true} />
          );
        }
        const shouldContinue = connectorPath[level] ?? false;
        return (
          <ConnectorBlock key={level} drawVertical={shouldContinue} drawElbow={false} />
        );
      })}

      <div
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? () => onNodeClick!(node.id) : undefined}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onNodeClick!(node.id);
                }
              }
            : undefined
        }
        aria-current={node.isCurrent ? 'true' : undefined}
        className={clsx(
          'flex-1 flex items-center gap-2 py-1.5 px-2 rounded-lg transition-colors min-w-0',
          node.isCurrent && [
            'bg-primary-custom-100 dark:bg-dark-bg-custom',
            'ring-1 ring-primary-custom-300 dark:ring-dark-border-custom',
          ],
          isClickable && 'cursor-pointer hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
        )}
      >
        <SmallDocIcon className="flex-shrink-0 text-content-secondary dark:text-dark-content-secondary" />

        <span className="text-xs font-medium text-content-secondary dark:text-dark-content-secondary flex-shrink-0">
          {node.classLabel}
        </span>

        <span className="text-sm font-semibold text-content-primary dark:text-dark-content-primary flex-shrink-0">
          {node.number}
        </span>

        <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-zinc-100 text-zinc-600 flex-shrink-0">
          {node.status}
        </span>

        {node.quantityOrdered !== undefined && node.quantityFulfilled !== undefined && (
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-content-secondary dark:text-dark-content-secondary tabular-nums">
              {node.quantityFulfilled} / {node.quantityOrdered}
            </span>
            <span className="w-16 h-1 rounded-full bg-border-primary dark:bg-dark-border-primary overflow-hidden flex-shrink-0">
              <span
                className="block h-full bg-primary-custom-500 rounded-full"
                style={{ width: `${pct}%` }}
              />
            </span>
          </span>
        )}

        {node.isCurrent && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-primary-custom-600 dark:text-dark-content-custom flex-shrink-0">
            {t('master_document.crossing.current')}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * DocumentTraceabilityTree — muestra el árbol de trazabilidad de un documento,
 * incluyendo ancestros (padres) y descendientes (hijos) conectados con líneas visuales.
 *
 * @see docs/colors.md — Tokens de color del sistema
 * @see docs/dark-mode-guide.md — Estrategia de dark mode
 *
 * @example
 * ```tsx
 * <DocumentTraceabilityTree
 *   tree={treeData}
 *   onNodeClick={(id) => navigate(`/documents/${id}`)}
 * />
 * ```
 */
export function DocumentTraceabilityTree({
  tree,
  onNodeClick,
  loading = false,
  error,
  onRetry,
  className,
}: DocumentTraceabilityTreeProps) {
  const { t } = useMasterDocumentTranslation();
  const isEmpty =
    !loading &&
    !error &&
    (tree === null || (tree.parents.length === 0 && tree.children.length === 0 && !tree.isCurrent));

  return (
    <div
      className={clsx(
        'bg-bg-primary rounded-xl overflow-hidden',
        'shadow-[0_1px_3px_rgba(60,64,67,.15),0_1px_2px_rgba(60,64,67,.10)]',
        'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
        className,
      )}
    >
      <div className="flex items-center px-4 py-3 bg-background-secondary dark:bg-dark-bg-secondary">
        <h3 className="text-sm font-bold text-content-primary dark:text-dark-content-primary">
          {t('master_document.document.traceability')}
        </h3>
      </div>

      <div className="p-4">
        {loading && <LoadingSkeleton />}

        {!loading && error && <ErrorState error={error} onRetry={onRetry} />}

        {!loading && !error && isEmpty && <EmptyState />}

        {!loading && !error && !isEmpty && tree !== null && (
          <div className="space-y-0.5">
            {buildDisplayList(tree).map((dn) => (
              <TreeNodeRow key={dn.node.id} displayNode={dn} onNodeClick={onNodeClick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
